import { Injectable, computed, inject } from "@angular/core";
import { LvfAppModule, LvfAppPlatform, LvfSideMenuType } from "@kiraah-shared/common";
import { MobileAppCacheService } from "../cache/mobile-app-cache.service";
import { TenantSideMenu, TenantSideMenuItem } from "../models/mobile-profile.models";
import { mobileCanAccessMenuItem, mobileCheckOperationAccess } from "../profile/helpers/mobile-operation-access";
import { MobileProfileService } from "../profile/services/mobile-profile.service";
import { MobileAuthStore } from "../state/mobile-auth.store";
import { MobileProfileStore } from "../state/mobile-profile.store";
import {
  MobileBottomNavItem,
  MobileDrawerMenuItem,
  MobileDrawerSection,
  MobileMenuTarget,
  MobileQuickAccessItem,
} from "./mobile-navigation.models";
import {
  ACADEMY_MOBILE_APP_CODE,
  LEGACY_ELEARNING_MOBILE_APP_CODE,
  resolveMobileElearningQuizRoute,
  resolveMobileElearningSessionsRoute,
} from "../elearning-player/mobile-elearning-quiz-route";
import { MOBILE_BASE_PATH, mobilePath } from "./mobile-routes.constants";
import {
  isDerivedPortalMobileAppCode,
  resolvePortalFallbackBottomNavItems,
} from "./mobile-portal-navigation.helper";

const profileNavItem: MobileBottomNavItem = {
  LabelKey: "nav.profile",
  Icon: "person",
  Route: mobilePath("profile"),
  Label: "Profil",
};

const SIGNATURE_ROUTE = mobilePath("signature");
const SIGNATURE_URL_PATTERN = /(^|\/)e?[\-_]?signatures?(\/|$|\?)/i;
const PRESENCE_ROUTE = mobilePath("presence");
const PRESENCE_URL_PATTERN = /(^|\/)(presence-sign|presence)(\/|$|\?)/i;
const ELEARNING_PLAYER_ROUTE = mobilePath("elearning");
const ELEARNING_STUDENT_SESSIONS_ROUTE = mobilePath("elearning/student/sessions");
const ELEARNING_STUDENT_TASKS_ROUTE = mobilePath("elearning/student/tasks");
const ELEARNING_STUDENT_DOCUMENTS_ROUTE = mobilePath("elearning/student/documents");
const ELEARNING_MENTOR_SESSIONS_ROUTE = mobilePath("elearning/mentor/sessions");
const ELEARNING_MENTOR_TASKS_ROUTE = mobilePath("elearning/mentor/tasks");
const ELEARNING_MENTOR_DOCUMENTS_ROUTE = mobilePath("elearning/mentor/documents");
const ELEARNING_CONTACT_SESSIONS_ROUTE = mobilePath("elearning/contact/sessions");
const ELEARNING_CONTACT_TASKS_ROUTE = mobilePath("elearning/contact/tasks");
const ELEARNING_CONTACT_DOCUMENTS_ROUTE = mobilePath("elearning/contact/documents");
const ELEARNING_HUB_SESSIONS_ROUTE = mobilePath("elearning/sessions");
const ELEARNING_STUDENT_SURVEYS_ROUTE = mobilePath("elearning/student/surveys");
const ELEARNING_MENTOR_SURVEYS_ROUTE = mobilePath("elearning/mentor/surveys");
const ELEARNING_CONTACT_SURVEYS_ROUTE = mobilePath("elearning/contact/surveys");
const EAUDIT_MISSIONS_ROUTE = mobilePath("eaudit/missions");
const LEGACY_TASKS_ROUTE = mobilePath("tasks");
const CHANTIER_MISSIONS_ROUTE = mobilePath("chantier/missions");
const EPROJET_PROJECTS_ROUTE = mobilePath("eprojet/projets");
/** Liste kilométrages équipement (route mobile canonique). */
const EQUIPMENT_GLOBAL_KILOMETRAGE_ROUTE = mobilePath("eequipement/kilometrage");
/** Liste équipements (route mobile canonique). */
const EQUIPMENT_FLEET_ROUTE = mobilePath("eequipement/vehicles");
/** Liste assignations équipement (route mobile canonique). */
const EQUIPMENT_ASSIGNMENTS_ROUTE = mobilePath("eequipement/assignations");
/** Liste maintenances équipement (route mobile canonique). */
const EQUIPMENT_MAINTENANCES_ROUTE = mobilePath("eequipement/maintenance");
/** Liste contraventions équipement (route mobile canonique). */
const EQUIPMENT_CONTRAVENTIONS_ROUTE = mobilePath("eequipement/contraventions");
/** Liste chauffeurs équipement (équivalent web `#/driver/list`). */
const EQUIPMENT_DRIVERS_ROUTE = mobilePath("eequipement/chauffeurs");
const LEGACY_ELEARNING_QUIZ_UNDER_MOBILE = /\/elearning\/quiz(\/|$|\?)/i;
const BOTTOM_NAV_MAX_TASKBAR = 4;

@Injectable({ providedIn: "root" })
export class MobileAppContextService {
  private readonly profileStore = inject(MobileProfileStore);
  private readonly cacheService = inject(MobileAppCacheService);
  private readonly authStore = inject(MobileAuthStore);
  private readonly profileService = inject(MobileProfileService);

  readonly availableApplications = computed(() => this.profileStore.availableApplications());
  readonly activeApplication = computed(() => this.profileStore.activeApplication());

  readonly drawerSections = computed<MobileDrawerSection[]>(() => {
    const activeApp = this.profileStore.activeApplication();
    if (!activeApp?.Menus?.length) {
      return [];
    }

    const identity = this.authStore.authResult()?.ProfilData?.Identity as
      | { IsRoot?: boolean; IsAdmin?: boolean }
      | undefined;
    const profileData = this.profileStore.profile();
    const activeTenantAppUid = this.profileStore.activeTenantAppUid();

    const appCode = activeApp.Code;
    return [...activeApp.Menus]
      .filter((menu) => menu.Platform !== LvfAppPlatform.WEB)
      .filter((menu) => menu.IsActive !== false)
      .sort((left, right) => (left.Order ?? 0) - (right.Order ?? 0))
      .map((menu) => this.mapDrawerSection(menu, profileData, activeTenantAppUid, identity))
      .filter((section): section is MobileDrawerSection => !!section);
  });

  readonly bottomNavItems = computed<MobileBottomNavItem[]>(() => {
    if (!this.profileStore.activeApplication()) {
      return [profileNavItem];
    }

    let taskbarItems = this.dedupeBottomNavTaskbarItems(
      this.drawerSections().flatMap((section) =>
        section.Items
          .filter((item) => item.IsTaskbar)
          .map((item) => this.toBottomNavItem(item, section))
      )
    );

    const appCode = this.profileStore.activeApplication()?.Code;
    if (taskbarItems.length === 0 && isDerivedPortalMobileAppCode(appCode)) {
      taskbarItems = resolvePortalFallbackBottomNavItems(
        this.drawerSections(),
        (route) => this.isElearningSessionsRoute(route),
        (item) => this.finalizeSessionsBottomNavItem(item)
      );
    }

    return [...taskbarItems.slice(0, BOTTOM_NAV_MAX_TASKBAR), profileNavItem];
  });

  readonly quickAccessItems = computed<MobileQuickAccessItem[]>(() => {
    const activeApp = this.profileStore.activeApplication();
    if (!activeApp?.Menus?.length) {
      return [];
    }

    const identity = this.authStore.authResult()?.ProfilData?.Identity as
      | { IsRoot?: boolean; IsAdmin?: boolean }
      | undefined;
    const profileData = this.profileStore.profile();
    const activeTenantAppUid = this.profileStore.activeTenantAppUid();

    return activeApp.Menus.flatMap((menu) =>
      [...(menu.Items ?? [])]
        .filter((item) => item.Platform !== LvfAppPlatform.WEB)
        .filter((item) => item.IsActive !== false)
        .filter((item) => (item.LvfSideMenuType ?? LvfSideMenuType.BUSINESS) === LvfSideMenuType.QUICK_ACCESS)
        .filter((item) => this.canAccessMenuItem(profileData, activeTenantAppUid, identity, item))
        .map((item) => ({ order: item.Order ?? 0, entry: this.mapQuickAccessItem(item) }))
        .filter((row): row is { order: number; entry: MobileQuickAccessItem } => !!row.entry)
        .map((row) => ({ ...row.entry, order: row.order }))
    )
      .sort((left, right) => left.order - right.order)
      .map(({ order: _order, ...entry }) => entry);
  });

  /**
   * Prod tenants may expose hub + student (or mentor) session entries as separate MOBILE_TASKBAR
   * items with the same label. Collapse to one tab (integration behaviour).
   */
  private dedupeBottomNavTaskbarItems(items: MobileBottomNavItem[]): MobileBottomNavItem[] {
    const out: MobileBottomNavItem[] = [];
    let sessionsIndex = -1;

    for (const item of items) {
      if (this.isElearningSessionsRoute(item.Route)) {
        if (sessionsIndex < 0) {
          sessionsIndex = out.length;
          out.push(item);
        } else {
          out[sessionsIndex] = this.mergeSessionsBottomNavItems(out[sessionsIndex], item);
        }
        continue;
      }
      out.push(item);
    }

    if (sessionsIndex >= 0) {
      out[sessionsIndex] = this.finalizeSessionsBottomNavItem(out[sessionsIndex]);
    }

    return out;
  }

  private isElearningSessionsRoute(route: string): boolean {
    const norm = this.normalizeComparableUrl(route);
    const bases = [
      ELEARNING_HUB_SESSIONS_ROUTE,
      ELEARNING_STUDENT_SESSIONS_ROUTE,
      ELEARNING_MENTOR_SESSIONS_ROUTE,
      ELEARNING_CONTACT_SESSIONS_ROUTE,
    ].map((baseRoute) => baseRoute.toLowerCase());
    return bases.some((base) => norm === base || norm.startsWith(`${base}/`));
  }

  private mergeSessionsBottomNavItems(
    kept: MobileBottomNavItem,
    other: MobileBottomNavItem
  ): MobileBottomNavItem {
    return {
      ...kept,
      ShortLabel: kept.ShortLabel?.trim() || other.ShortLabel?.trim() || kept.ShortLabel,
      Icon: kept.Icon || other.Icon,
      Label: kept.Label || other.Label,
      LabelKey: kept.LabelKey || other.LabelKey,
    };
  }

  private finalizeSessionsBottomNavItem(item: MobileBottomNavItem): MobileBottomNavItem {
    const active = this.profileStore.activeApplication();
    const canonicalRoute = this.getCanonicalElearningSessionsRoute();
    const code = active?.Code?.trim().toLowerCase();
    const short =
      item.ShortLabel?.trim() ||
      (code === ACADEMY_MOBILE_APP_CODE || code === LEGACY_ELEARNING_MOBILE_APP_CODE ? "Sessions" : undefined);

    return {
      ...item,
      Route: canonicalRoute,
      ...(short ? { ShortLabel: short } : {}),
    };
  }

  private toBottomNavItem(
    item: MobileDrawerMenuItem,
    section: MobileDrawerSection
  ): MobileBottomNavItem {
    const taskbarCount = section.Items.filter((entry) => entry.IsTaskbar).length;
    return {
      LabelKey: item.LabelKey,
      Icon: item.Icon || section.Icon || "apps",
      Route: item.Route,
      Label: item.Label || section.Label,
      ShortLabel: taskbarCount > 1 ? undefined : section.ShortLabel,
    };
  }

  private mapDrawerSection(
    menu: TenantSideMenu,
    profileData: ReturnType<MobileProfileStore["profile"]>,
    activeTenantAppUid: string | null,
    identity: { IsRoot?: boolean; IsAdmin?: boolean } | undefined
  ): MobileDrawerSection | null {
    const items = [...(menu.Items ?? [])]
      .filter((item) => item.Platform !== LvfAppPlatform.WEB)
      .filter((item) => item.IsActive !== false)
      .filter((item) => (item.LvfSideMenuType ?? LvfSideMenuType.BUSINESS) !== LvfSideMenuType.QUICK_ACCESS)
      .filter((item) => this.canAccessMenuItem(profileData, activeTenantAppUid, identity, item))
      .sort((left, right) => (left.Order ?? 0) - (right.Order ?? 0))
      .map((item) => this.mapDrawerItem(item))
      .filter((item): item is MobileDrawerMenuItem => !!item);
    const dedupedItems = this.dedupeDrawerSectionSessionItems(items);

    if (dedupedItems.length === 0) {
      return null;
    }

    const section: MobileDrawerSection = {
      Uid: String(menu.Uid ?? ""),
      Label: menu.Label ?? "",
      LabelKey: this.normalizeDrawerMenuTranslationKey(menu.TranslationKey ?? `mobile.menu.${menu.Uid}`),
      Icon: menu.MobileIcon ?? menu.Icon ?? "apps",
      ShortLabel: menu.MobileShortLabel?.trim() || undefined,
      Order: menu.Order ?? 0,
      Items: dedupedItems,
    };

    return this.enrichContactPortalTrainingsDrawerSection(
      this.enrichStudentPortalTrainingsDrawerSection(
        this.enrichMentorPortalTrainingsDrawerSection(
          this.applyContactPortalDrawerSection(
            this.applyStudentPortalDrawerSection(this.applyMentorPortalDrawerSection(section))
          ),
          profileData,
          activeTenantAppUid
        ),
        profileData,
        activeTenantAppUid
      ),
      profileData,
      activeTenantAppUid
    );
  }

  /**
   * Prod may expose hub + student (or mentor) session links in one drawer section.
   * Collapse to a single entry (same as bottom nav dedupe).
   */
  private dedupeDrawerSectionSessionItems(items: MobileDrawerMenuItem[]): MobileDrawerMenuItem[] {
    const out: MobileDrawerMenuItem[] = [];
    let sessionsIndex = -1;

    for (const item of items) {
      if (this.isElearningSessionsRoute(item.Route)) {
        if (sessionsIndex < 0) {
          sessionsIndex = out.length;
          out.push(item);
        } else {
          out[sessionsIndex] = this.mergeSessionsDrawerItems(out[sessionsIndex], item);
        }
        continue;
      }
      out.push(item);
    }

    if (sessionsIndex >= 0) {
      out[sessionsIndex] = this.applyContactPortalSessionsMenuLabel(
        this.applyStudentPortalSessionsMenuLabel(
          this.applyMentorPortalSessionsMenuLabel(this.finalizeSessionsDrawerItem(out[sessionsIndex]))
        )
      );
    }

    return out;
  }

  private mergeSessionsDrawerItems(
    kept: MobileDrawerMenuItem,
    other: MobileDrawerMenuItem
  ): MobileDrawerMenuItem {
    return {
      ...kept,
      Order: Math.min(kept.Order, other.Order),
      IsDefaultHome: kept.IsDefaultHome || other.IsDefaultHome,
      IsTaskbar: kept.IsTaskbar || other.IsTaskbar,
      Icon: kept.Icon || other.Icon,
      Label: kept.Label || other.Label,
      LabelKey: kept.LabelKey || other.LabelKey,
    };
  }

  private finalizeSessionsDrawerItem(item: MobileDrawerMenuItem): MobileDrawerMenuItem {
    return {
      ...item,
      Route: this.getCanonicalElearningSessionsRoute(),
    };
  }

  private isAcademyMentorPortalMobileApp(): boolean {
    return this.profileStore.activeApplication()?.Code?.trim().toLowerCase() === "academy-mentor-mobile";
  }

  private isAcademyStudentPortalMobileApp(): boolean {
    return this.profileStore.activeApplication()?.Code?.trim().toLowerCase() === "academy-student-mobile";
  }

  private isAcademyEnterprisePortalMobileApp(): boolean {
    return this.profileStore.activeApplication()?.Code?.trim().toLowerCase() === "academy-enterprise-mobile";
  }

  /** Anciens seeds menu enterprise utilisaient `our_trainings` sans préfixe i18n. */
  private normalizeDrawerMenuTranslationKey(labelKey: string): string {
    if (labelKey === "our_trainings") {
      return "app.menu.our_trainings";
    }
    return labelKey;
  }

  /** Tenants seeded before « Mes formations » still expose « Mes sessions » in API menus. */
  private applyMentorPortalSessionsMenuLabel(item: MobileDrawerMenuItem): MobileDrawerMenuItem {
    if (!this.isAcademyMentorPortalMobileApp()) {
      return item;
    }
    if (
      this.normalizeComparableUrl(item.Route) !== this.normalizeComparableUrl(ELEARNING_MENTOR_SESSIONS_ROUTE)
    ) {
      return item;
    }
    return {
      ...item,
      Label: "",
      LabelKey: "app.menu.my_trainings",
    };
  }

  private applyStudentPortalSessionsMenuLabel(item: MobileDrawerMenuItem): MobileDrawerMenuItem {
    if (!this.isAcademyStudentPortalMobileApp()) {
      return item;
    }
    if (
      this.normalizeComparableUrl(item.Route) !== this.normalizeComparableUrl(ELEARNING_STUDENT_SESSIONS_ROUTE)
    ) {
      return item;
    }
    return { ...item, Label: "", LabelKey: "app.menu.my_trainings" };
  }

  private applyContactPortalSessionsMenuLabel(item: MobileDrawerMenuItem): MobileDrawerMenuItem {
    if (!this.isAcademyEnterprisePortalMobileApp()) {
      return item;
    }
    if (
      this.normalizeComparableUrl(item.Route) !== this.normalizeComparableUrl(ELEARNING_CONTACT_SESSIONS_ROUTE)
    ) {
      return item;
    }
    return { ...item, Label: "", LabelKey: "app.menu.our_trainings" };
  }

  private applyContactPortalDrawerSection(section: MobileDrawerSection): MobileDrawerSection {
    if (!this.isAcademyEnterprisePortalMobileApp()) {
      return section;
    }
    const contactSessionsRoute = this.normalizeComparableUrl(ELEARNING_CONTACT_SESSIONS_ROUTE);
    const hasContactSessions = section.Items.some(
      (item) => this.normalizeComparableUrl(item.Route) === contactSessionsRoute
    );
    if (!hasContactSessions || !this.isPortalTrainingsDrawerSection(section)) {
      return section;
    }
    return { ...section, Label: "", LabelKey: "app.menu.our_trainings" };
  }

  private applyStudentPortalDrawerSection(section: MobileDrawerSection): MobileDrawerSection {
    if (!this.isAcademyStudentPortalMobileApp()) {
      return section;
    }
    const studentSessionsRoute = this.normalizeComparableUrl(ELEARNING_STUDENT_SESSIONS_ROUTE);
    const hasStudentSessions = section.Items.some(
      (item) => this.normalizeComparableUrl(item.Route) === studentSessionsRoute
    );
    if (!hasStudentSessions || !this.isPortalTrainingsDrawerSection(section)) {
      return section;
    }
    return { ...section, Label: "", LabelKey: "app.menu.my_trainings" };
  }

  private applyMentorPortalDrawerSection(section: MobileDrawerSection): MobileDrawerSection {
    if (!this.isAcademyMentorPortalMobileApp()) {
      return section;
    }
    const mentorSessionsRoute = this.normalizeComparableUrl(ELEARNING_MENTOR_SESSIONS_ROUTE);
    const hasMentorSessions = section.Items.some(
      (item) => this.normalizeComparableUrl(item.Route) === mentorSessionsRoute
    );
    if (!hasMentorSessions || !this.isPortalTrainingsDrawerSection(section)) {
      return section;
    }
    return {
      ...section,
      Label: "",
      LabelKey: "app.menu.my_trainings",
    };
  }

  private isPortalTrainingsDrawerSection(section: MobileDrawerSection): boolean {
    const label = section.Label?.trim().toLowerCase();
    return (
      label === "mes sessions" ||
      label === "mes formations" ||
      label === "nos formations" ||
      section.LabelKey === "app.menu.my_trainings" ||
      section.LabelKey === "app.menu.our_trainings" ||
      section.LabelKey === "our_trainings"
    );
  }

  /** Tenants seeded before mentor task/document mobile entries still expose sessions only. */
  private enrichMentorPortalTrainingsDrawerSection(
    section: MobileDrawerSection,
    profileData: ReturnType<MobileProfileStore["profile"]>,
    activeTenantAppUid: string | null
  ): MobileDrawerSection {
    if (!this.isAcademyMentorPortalMobileApp()) {
      return section;
    }

    const mentorSessionsRoute = this.normalizeComparableUrl(ELEARNING_MENTOR_SESSIONS_ROUTE);
    const hasMentorSessions = section.Items.some(
      (item) => this.normalizeComparableUrl(item.Route) === mentorSessionsRoute
    );
    if (!hasMentorSessions || !this.isPortalTrainingsDrawerSection(section)) {
      return section;
    }

    const candidates: MobileDrawerMenuItem[] = [
      {
        Uid: "mentor-portal-tasks",
        Label: "",
        LabelKey: "app.lvfappmodule.elearning_mentor_task",
        Icon: "checklist_rtl",
        Route: ELEARNING_MENTOR_TASKS_ROUTE,
        Order: 2,
        IsDefaultHome: false,
        IsTaskbar: false,
      },
      {
        Uid: "mentor-portal-documents",
        Label: "",
        LabelKey: "app.lvfappmodule.elearning_mentor_document",
        Icon: "description",
        Route: ELEARNING_MENTOR_DOCUMENTS_ROUTE,
        Order: 3,
        IsDefaultHome: false,
        IsTaskbar: false,
      },
    ];

    const modules = [LvfAppModule.ELEARNING_MENTOR_TASK, LvfAppModule.ELEARNING_MENTOR_DOCUMENT];
    let items = [...section.Items];

    candidates.forEach((candidate, index) => {
      const routeKey = this.normalizeComparableUrl(candidate.Route);
      if (items.some((item) => this.normalizeComparableUrl(item.Route) === routeKey)) {
        return;
      }
      if (!mobileCheckOperationAccess(profileData, activeTenantAppUid, modules[index])) {
        return;
      }
      items.push(candidate);
    });

    items = items.sort((left, right) => left.Order - right.Order);
    return { ...section, Items: items };
  }

  /** Même pattern formateur : entrées menu si seed API ancien. */
  private enrichStudentPortalTrainingsDrawerSection(
    section: MobileDrawerSection,
    profileData: ReturnType<MobileProfileStore["profile"]>,
    activeTenantAppUid: string | null
  ): MobileDrawerSection {
    if (!this.isAcademyStudentPortalMobileApp()) {
      return section;
    }
    const studentSessionsRoute = this.normalizeComparableUrl(ELEARNING_STUDENT_SESSIONS_ROUTE);
    const hasStudentSessions = section.Items.some(
      (item) => this.normalizeComparableUrl(item.Route) === studentSessionsRoute
    );
    if (!hasStudentSessions || !this.isPortalTrainingsDrawerSection(section)) {
      return section;
    }

    const candidates: MobileDrawerMenuItem[] = [
      {
        Uid: "student-portal-tasks",
        Label: "",
        LabelKey: "app.lvfappmodule.elearning_student_task",
        Icon: "checklist_rtl",
        Route: ELEARNING_STUDENT_TASKS_ROUTE,
        Order: 2,
        IsDefaultHome: false,
        IsTaskbar: false,
      },
      {
        Uid: "student-portal-documents",
        Label: "",
        LabelKey: "app.lvfappmodule.elearning_student_document",
        Icon: "description",
        Route: ELEARNING_STUDENT_DOCUMENTS_ROUTE,
        Order: 3,
        IsDefaultHome: false,
        IsTaskbar: false,
      },
    ];
    const modules = [LvfAppModule.ELEARNING_STUDENT_TASK, LvfAppModule.ELEARNING_STUDENT_DOCUMENT];
    let items = [...section.Items];
    candidates.forEach((candidate, index) => {
      const routeKey = this.normalizeComparableUrl(candidate.Route);
      if (items.some((item) => this.normalizeComparableUrl(item.Route) === routeKey)) {
        return;
      }
      if (!mobileCheckOperationAccess(profileData, activeTenantAppUid, modules[index])) {
        return;
      }
      items.push(candidate);
    });
    items = items.sort((left, right) => left.Order - right.Order);
    items = this.dedupeStudentPortalTrainingTaskItems(items);
    return { ...section, Items: items };
  }

  /** Aligné web enterprise : Nos formations + tâches + documents dans le drawer. */
  private enrichContactPortalTrainingsDrawerSection(
    section: MobileDrawerSection,
    profileData: ReturnType<MobileProfileStore["profile"]>,
    activeTenantAppUid: string | null
  ): MobileDrawerSection {
    if (!this.isAcademyEnterprisePortalMobileApp()) {
      return section;
    }
    const contactSessionsRoute = this.normalizeComparableUrl(ELEARNING_CONTACT_SESSIONS_ROUTE);
    const hasContactSessions = section.Items.some(
      (item) => this.normalizeComparableUrl(item.Route) === contactSessionsRoute
    );
    if (!hasContactSessions || !this.isPortalTrainingsDrawerSection(section)) {
      return section;
    }

    const candidates: MobileDrawerMenuItem[] = [
      {
        Uid: "contact-portal-tasks",
        Label: "",
        LabelKey: "app.lvfappmodule.elearning_third_part_contact_task",
        Icon: "checklist_rtl",
        Route: ELEARNING_CONTACT_TASKS_ROUTE,
        Order: 2,
        IsDefaultHome: false,
        IsTaskbar: false,
      },
      {
        Uid: "contact-portal-documents",
        Label: "",
        LabelKey: "app.lvfappmodule.elearning_third_part_contact_document",
        Icon: "description",
        Route: ELEARNING_CONTACT_DOCUMENTS_ROUTE,
        Order: 3,
        IsDefaultHome: false,
        IsTaskbar: false,
      },
    ];
    const modules = [
      LvfAppModule.ELEARNING_THIRD_PART_CONTACT_TASK,
      LvfAppModule.ELEARNING_THIRD_PART_CONTACT_DOCUMENT,
    ];
    let items = [...section.Items];
    candidates.forEach((candidate, index) => {
      const routeKey = this.normalizeComparableUrl(candidate.Route);
      if (items.some((item) => this.normalizeComparableUrl(item.Route) === routeKey)) {
        return;
      }
      if (!mobileCheckOperationAccess(profileData, activeTenantAppUid, modules[index])) {
        return;
      }
      items.push(candidate);
    });
    items = items.sort((left, right) => left.Order - right.Order);
    return { ...section, Items: items };
  }

  /** Évite Mes tâches en double (elearning + eAudit missions) dans le hub formations apprenant. */
  private dedupeStudentPortalTrainingTaskItems(items: MobileDrawerMenuItem[]): MobileDrawerMenuItem[] {
    const canonicalTasksRoute = this.normalizeComparableUrl(ELEARNING_STUDENT_TASKS_ROUTE);
    const eauditMissionsRoute = this.normalizeComparableUrl(EAUDIT_MISSIONS_ROUTE);
    const studentTaskLabelKey = "app.lvfappmodule.elearning_student_task";
    let keptTrainingTasks = false;

    return items.filter((item) => {
      const routeKey = this.normalizeComparableUrl(item.Route);
      const isStudentTasksEntry =
        item.LabelKey === studentTaskLabelKey || item.Label?.trim().toLowerCase() === "mes tâches";
      if (!isStudentTasksEntry) {
        return true;
      }
      if (routeKey === eauditMissionsRoute || routeKey.startsWith(`${eauditMissionsRoute}/`)) {
        return false;
      }
      const isTrainingTasksRoute =
        routeKey === canonicalTasksRoute || routeKey.startsWith(`${canonicalTasksRoute}/`);
      if (isTrainingTasksRoute) {
        if (keptTrainingTasks) {
          return false;
        }
        keptTrainingTasks = true;
        return true;
      }
      if (keptTrainingTasks) {
        return false;
      }
      keptTrainingTasks = true;
      return true;
    });
  }

  private getCanonicalElearningSessionsRoute(): string {
    return resolveMobileElearningSessionsRoute(
      this.profileStore.profile(),
      this.profileStore.activeTenantAppUid(),
      (this.profileStore.activeApplication() as { Code?: string } | undefined)?.Code
    );
  }

  private mapQuickAccessItem(item: TenantSideMenuItem): MobileQuickAccessItem | null {
    const mapped = this.mapDrawerItem(item);
    if (!mapped) {
      return null;
    }

    return {
      Label: mapped.Label,
      LabelKey: mapped.LabelKey,
      Icon: mapped.Icon,
      Route: mapped.Route,
    };
  }

  private mapDrawerItem(item: TenantSideMenuItem): MobileDrawerMenuItem | null {
    const url = item.Url?.trim();
    if (!url) {
      return null;
    }

    const normalizedRoute = this.normalizeElearningLearningCampaignUrl(
      this.normalizeElearningTrainingSessionUrl(
        this.normalizeElearningPortalTasksDocumentsUrl(
          this.normalizeElearningQuizLegacyUrl(this.normalizeMobileMenuUrl(url))
        )
      )
    );
    const withEchantierHub = this.normalizeEchantierHubToChantierMissions(normalizedRoute);
    const withEprojet = this.normalizeTenantEprojetProjectsUrl(withEchantierHub);
    const withEauditMissions = this.normalizeTenantEauditMissionsUrl(withEprojet);
    const withTasks = this.normalizeTenantTasksUrl(withEauditMissions);
    const withChantierMissions = this.normalizeTenantChantierMissionsUrl(withTasks);
    const withEquipmentLegacy = this.normalizeLegacyEquipmentMenuUrl(withChantierMissions);
    const withEquipmentKilometrage = this.normalizeEquipmentKilometerUrl(withEquipmentLegacy);
    const withEquipmentAssignments = this.normalizeEquipmentAssignmentUrl(withEquipmentKilometrage);
    const withEquipmentMaintenances = this.normalizeEquipmentMaintenanceUrl(withEquipmentAssignments);
    const withEquipmentContraventions = this.normalizeEquipmentContraventionUrl(withEquipmentMaintenances);
    const withEquipmentDrivers = this.normalizeEquipmentDriverUrl(withEquipmentContraventions);
    const withEquipmentFleet = this.normalizeEquipmentFleetUrl(withEquipmentDrivers);
    const withPresence = PRESENCE_URL_PATTERN.test(url) ? PRESENCE_ROUTE : withEquipmentFleet;
    const finalRoute = SIGNATURE_URL_PATTERN.test(url) ? SIGNATURE_ROUTE : withPresence;

    return {
      Uid: String(item.Uid ?? ""),
      Label: item.Label ?? "",
      LabelKey: this.normalizeDrawerMenuTranslationKey(
        item.TranslationKey ?? `mobile.menuitem.${item.Uid}`
      ),
      Icon: item.Icon ?? "apps",
      Route: finalRoute,
      Order: item.Order ?? 0,
      IsDefaultHome: !!item.IsDefaultHome,
      IsTaskbar:
        (item.LvfSideMenuType ?? LvfSideMenuType.BUSINESS) === LvfSideMenuType.MOBILE_TASKBAR,
    };
  }

  resolveMenuTarget(menuUid: string): MobileMenuTarget | null {
    const section = this.findDrawerSection(menuUid);
    if (!section || section.Items.length === 0) {
      return null;
    }

    if (section.Items.length === 1) {
      return {
        MenuUid: section.Uid,
        RequiresChoice: false,
        Url: section.Items[0].Route,
        Items: section.Items,
      };
    }

    return {
      MenuUid: section.Uid,
      RequiresChoice: true,
      Url: mobilePath(`menu/${section.Uid}`),
      Items: section.Items,
    };
  }

  getDrawerSection(menuUid: string): MobileDrawerSection | null {
    return this.findDrawerSection(menuUid);
  }

  getMenuItems(menuUid: string): MobileDrawerMenuItem[] {
    return this.findDrawerSection(menuUid)?.Items ?? [];
  }

  resolveMenuItemByUrl(url: string): MobileDrawerMenuItem | null {
    const expected = this.normalizeComparableUrl(url);
    for (const section of this.drawerSections()) {
      const hit = section.Items.find((item) => this.normalizeComparableUrl(item.Route) === expected);
      if (hit) {
        return hit;
      }
    }
    return null;
  }

  private normalizeTenantEprojetProjectsUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const normalizedPath = pathPart.replace(/\/+$/, "");
    const hub = `${MOBILE_BASE_PATH}/eprojet`.toLowerCase();
    const lowerPath = normalizedPath.toLowerCase();
    if (lowerPath !== hub && !lowerPath.startsWith(`${hub}/`)) {
      return route;
    }
    const projectsPrefix = EPROJET_PROJECTS_ROUTE.toLowerCase();
    const legacyMissionsPrefix = mobilePath("eprojet/missions").toLowerCase();
    if (lowerPath === legacyMissionsPrefix || lowerPath.startsWith(`${legacyMissionsPrefix}/`)) {
      const suffix = normalizedPath.slice(legacyMissionsPrefix.length);
      const target = `${EPROJET_PROJECTS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (lowerPath === projectsPrefix || lowerPath.startsWith(`${projectsPrefix}/`)) {
      return queryPart ? `${normalizedPath}?${queryPart}` : route;
    }
    const segments = normalizedPath.split("/").filter(Boolean);
    const lowerSegments = segments.map((segment) => segment.toLowerCase());
    const eprojetIdx = lowerSegments.lastIndexOf("eprojet");
    if (eprojetIdx < 0) {
      return route;
    }
    const afterHub = segments.slice(eprojetIdx + 1);
    const first = afterHub[0]?.toLowerCase();
    if (afterHub.length === 0 || first === "tasks" || first === "missions") {
      const rest = first === "tasks" || first === "missions" ? afterHub.slice(1) : [];
      const suffix = rest.length ? `/${rest.join("/")}` : "";
      const target = mobilePath(`eprojet/projets${suffix}`.replace(/^\//, ""));
      return queryPart ? `${target}?${queryPart}` : target;
    }
    return route;
  }

  private normalizeEchantierHubToChantierMissions(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    const lower = path.toLowerCase();
    const hub = `${MOBILE_BASE_PATH}/echantier`.toLowerCase();
    if (lower === hub || lower.startsWith(`${hub}/construction-sites`)) {
      const target = CHANTIER_MISSIONS_ROUTE;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    return route;
  }

  private normalizeTenantEauditMissionsUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const normalizedPath = pathPart.replace(/\/+$/, "");
    const hub = `${MOBILE_BASE_PATH}/eaudit`.toLowerCase();
    const lowerPath = normalizedPath.toLowerCase();
    if (lowerPath !== hub && !lowerPath.startsWith(`${hub}/`)) {
      return route;
    }
    const missionsPrefix = EAUDIT_MISSIONS_ROUTE.toLowerCase();
    if (lowerPath === missionsPrefix || lowerPath.startsWith(`${missionsPrefix}/`)) {
      return queryPart ? `${normalizedPath}?${queryPart}` : route;
    }
    const segments = normalizedPath.split("/").filter(Boolean);
    const lowerSegments = segments.map((segment) => segment.toLowerCase());
    const eauditIdx = lowerSegments.lastIndexOf("eaudit");
    if (eauditIdx < 0) {
      return route;
    }
    const afterHub = segments.slice(eauditIdx + 1);
    const first = afterHub[0]?.toLowerCase();
    if (afterHub.length === 0 || first === "tasks") {
      const rest = first === "tasks" ? afterHub.slice(1) : [];
      const suffix = rest.length ? `/${rest.join("/")}` : "";
      const target = mobilePath(`eaudit/missions${suffix}`.replace(/^\//, ""));
      return queryPart ? `${target}?${queryPart}` : target;
    }
    return route;
  }

  private normalizeTenantTasksUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const normalizedPath = pathPart.replace(/\/+$/, "");
    const eauditMissionsPrefix = EAUDIT_MISSIONS_ROUTE.toLowerCase();
    const lowerPath = normalizedPath.toLowerCase();
    if (this.isAcademyElearningPortalTaskDocumentRoute(lowerPath)) {
      return route;
    }
    if (lowerPath === eauditMissionsPrefix || lowerPath.startsWith(`${eauditMissionsPrefix}/`)) {
      return queryPart ? `${normalizedPath}?${queryPart}` : route;
    }
    const legacyTasksPrefix = LEGACY_TASKS_ROUTE.toLowerCase();
    if (lowerPath === legacyTasksPrefix || lowerPath.startsWith(`${legacyTasksPrefix}/`)) {
      const prefixSegments = legacyTasksPrefix.split("/").filter(Boolean);
      const pathSegments = normalizedPath.split("/").filter(Boolean);
      const afterTasks = pathSegments.slice(prefixSegments.length);
      const suffix = afterTasks.length ? `/${afterTasks.join("/")}` : "";
      const target = mobilePath(`eaudit/missions${suffix}`.replace(/^\//, ""));
      return queryPart ? `${target}?${queryPart}` : target;
    }
    const segments = normalizedPath.split("/").filter(Boolean);
    const tasksIndex = segments.findIndex((segment) => segment.toLowerCase() === "tasks");
    if (tasksIndex < 0) {
      return route;
    }
    const beforeTasks = segments.slice(0, tasksIndex);
    if (beforeTasks.some((segment) => segment.toLowerCase() === "eaudit")) {
      return route;
    }
    const afterTasks = segments.slice(tasksIndex + 1);
    const suffix = afterTasks.length ? `/${afterTasks.join("/")}` : "";
    const target = mobilePath(`eaudit/missions${suffix}`.replace(/^\//, ""));
    return queryPart ? `${target}?${queryPart}` : target;
  }

  private isAcademyElearningPortalTaskDocumentRoute(lowerPath: string): boolean {
    return (
      lowerPath.includes("/elearning/student/tasks") ||
      lowerPath.includes("/elearning/mentor/tasks") ||
      lowerPath.includes("/elearning/student/documents") ||
      lowerPath.includes("/elearning/mentor/documents") ||
      lowerPath.includes("/elearning/contact/tasks") ||
      lowerPath.includes("/elearning/contact/documents") ||
      /\/elearning\/student\/task(\/|$)/.test(lowerPath) ||
      /\/elearning\/mentor\/task(\/|$)/.test(lowerPath) ||
      /\/elearning\/contact\/task(\/|$)/.test(lowerPath) ||
      /\/elearning\/student\/document(\/|$)/.test(lowerPath) ||
      /\/elearning\/mentor\/document(\/|$)/.test(lowerPath) ||
      /\/elearning\/contact\/document(\/|$)/.test(lowerPath)
    );
  }

  /** Web `/elearning/student/task/list` → `/mobile/elearning/student/tasks` (pas eAudit missions). */
  private normalizeElearningPortalTasksDocumentsUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    const lower = path.toLowerCase();

    if (/\/elearning\/student\/task(\/|$)/i.test(lower)) {
      const target = ELEARNING_STUDENT_TASKS_ROUTE;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (/\/elearning\/mentor\/task(\/|$)/i.test(lower)) {
      const target = ELEARNING_MENTOR_TASKS_ROUTE;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (/\/elearning\/student\/document(\/|$)/i.test(lower)) {
      const target = ELEARNING_STUDENT_DOCUMENTS_ROUTE;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (/\/elearning\/mentor\/document(\/|$)/i.test(lower)) {
      const target = ELEARNING_MENTOR_DOCUMENTS_ROUTE;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (/\/elearning\/contact\/task(\/|$)/i.test(lower)) {
      const target = ELEARNING_CONTACT_TASKS_ROUTE;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (/\/elearning\/contact\/document(\/|$)/i.test(lower)) {
      const target = ELEARNING_CONTACT_DOCUMENTS_ROUTE;
      return queryPart ? `${target}?${queryPart}` : target;
    }

    return route;
  }

  /** Liens tenant type « …/missions » : même sous-arbre que les tâches, préfixe `/mobile/chantier/missions`. */
  private normalizeTenantChantierMissionsUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const normalizedPath = pathPart.replace(/\/+$/, "");
    const mobilePrefix = CHANTIER_MISSIONS_ROUTE.toLowerCase();
    const lowerPath = normalizedPath.toLowerCase();
    if (lowerPath === mobilePrefix || lowerPath.startsWith(`${mobilePrefix}/`)) {
      return queryPart ? `${normalizedPath}?${queryPart}` : route;
    }
    const segments = normalizedPath.split("/").filter(Boolean);
    const missionsIndex = segments.findIndex((segment) => segment.toLowerCase() === "missions");
    if (missionsIndex < 0) {
      return route;
    }
    const beforeMissions = segments.slice(0, missionsIndex).some((segment) => {
      const lowerSegment = segment.toLowerCase();
      return (
        lowerSegment.includes("chantier") ||
        lowerSegment.includes("construction") ||
        lowerSegment.includes("jobsite") ||
        lowerSegment.includes("echantier")
      );
    });
    if (!beforeMissions) {
      return route;
    }
    const afterMissions = segments.slice(missionsIndex + 1);
    const suffix = afterMissions.length ? `/${afterMissions.join("/")}` : "";
    const target = mobilePath(`chantier/missions${suffix}`.replace(/^\//, ""));
    return queryPart ? `${target}?${queryPart}` : target;
  }

  /** URLs seed legacy (`vehicle/list`, …) → routes mobiles canoniques. */
  private normalizeLegacyEquipmentMenuUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    const lower = path.toLowerCase();
    const legacyTargets: ReadonlyArray<{ pattern: RegExp; target: string }> = [
      { pattern: /\/eequipement\/vehicle\/list$/i, target: EQUIPMENT_FLEET_ROUTE },
      { pattern: /\/eequipement\/vehicle\/maintenance\/list$/i, target: EQUIPMENT_MAINTENANCES_ROUTE },
      { pattern: /\/eequipement\/vehicle\/assignment\/list$/i, target: EQUIPMENT_ASSIGNMENTS_ROUTE },
      { pattern: /\/eequipement\/vehicle\/kilometer\/list$/i, target: EQUIPMENT_GLOBAL_KILOMETRAGE_ROUTE },
      { pattern: /\/eequipement\/vehicle\/traffic-ticket\/list$/i, target: EQUIPMENT_CONTRAVENTIONS_ROUTE },
    ];

    for (const entry of legacyTargets) {
      if (entry.pattern.test(lower)) {
        return queryPart ? `${entry.target}?${queryPart}` : entry.target;
      }
    }

    return route;
  }

  /** Accepte uniquement les routes mobiles canoniques de kilométrage. */
  private normalizeEquipmentKilometerUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    const lower = path.toLowerCase();
    const mobileTarget = EQUIPMENT_GLOBAL_KILOMETRAGE_ROUTE.toLowerCase();
    if (lower === mobileTarget || lower.startsWith(`${mobileTarget}/`)) {
      return queryPart ? `${pathPart}?${queryPart}` : route;
    }
    return route;
  }

  /** Accepte uniquement les routes mobiles canoniques de flotte équipement. */
  private normalizeEquipmentFleetUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    const lower = path.toLowerCase();
    const mobileTarget = EQUIPMENT_FLEET_ROUTE.toLowerCase();
    if (lower === mobileTarget || lower.startsWith(`${mobileTarget}/`)) {
      return queryPart ? `${pathPart}?${queryPart}` : route;
    }
    return route;
  }

  /** Accepte uniquement les routes mobiles canoniques d'assignation équipement. */
  private normalizeEquipmentAssignmentUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    const lower = path.toLowerCase();
    const mobileTarget = EQUIPMENT_ASSIGNMENTS_ROUTE.toLowerCase();
    if (lower === mobileTarget || lower.startsWith(`${mobileTarget}/`)) {
      return queryPart ? `${pathPart}?${queryPart}` : route;
    }
    return route;
  }

  /** Accepte uniquement les routes mobiles canoniques de maintenance équipement. */
  private normalizeEquipmentMaintenanceUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    const lower = path.toLowerCase();
    const mobileTarget = EQUIPMENT_MAINTENANCES_ROUTE.toLowerCase();
    if (lower === mobileTarget || lower.startsWith(`${mobileTarget}/`)) {
      return queryPart ? `${pathPart}?${queryPart}` : route;
    }
    return route;
  }

  /** Accepte uniquement les routes mobiles canoniques de contravention équipement. */
  private normalizeEquipmentContraventionUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    const lower = path.toLowerCase();
    const mobileTarget = EQUIPMENT_CONTRAVENTIONS_ROUTE.toLowerCase();
    if (lower === mobileTarget || lower.startsWith(`${mobileTarget}/`)) {
      return queryPart ? `${pathPart}?${queryPart}` : route;
    }
    return route;
  }

  /**
   * URLs configurées côté tenant comme la SPA web (`#/driver/list`, `#/driver`)
   * → liste chauffeurs mobile sous E-Équipement.
   */
  private normalizeEquipmentDriverUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    const lower = path.toLowerCase();
    const mobileTarget = EQUIPMENT_DRIVERS_ROUTE.toLowerCase();
    if (lower === mobileTarget || lower.startsWith(`${mobileTarget}/`)) {
      return queryPart ? `${pathPart}?${queryPart}` : route;
    }
    if (/\/drivers?(\/(list)?)?$/i.test(lower)) {
      const target = EQUIPMENT_DRIVERS_ROUTE;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    return route;
  }

  private normalizeElearningQuizLegacyUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    if (LEGACY_ELEARNING_QUIZ_UNDER_MOBILE.test(path)) {
      const target = resolveMobileElearningQuizRoute(
        this.profileStore.profile(),
        this.profileStore.activeTenantAppUid(),
        this.profileStore.activeApplication()?.Code
      );
      return queryPart ? `${target}?${queryPart}` : target;
    }
    return route;
  }

  /** URLs web `/elearning/student/sessions/list` → `/mobile/elearning/student/sessions`. */
  private normalizeElearningTrainingSessionUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    const lower = path.toLowerCase();

    const studentList = `${ELEARNING_STUDENT_SESSIONS_ROUTE}/list`.toLowerCase();
    const mentorList = `${ELEARNING_MENTOR_SESSIONS_ROUTE}/list`.toLowerCase();
    const contactList = `${ELEARNING_CONTACT_SESSIONS_ROUTE}/list`.toLowerCase();
    const hubList = `${ELEARNING_HUB_SESSIONS_ROUTE}/list`.toLowerCase();
    if (lower === hubList || lower.startsWith(`${hubList}/`)) {
      const suffix = path.slice(ELEARNING_HUB_SESSIONS_ROUTE.length + "/list".length);
      const target = `${ELEARNING_HUB_SESSIONS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (lower === studentList || lower.startsWith(`${studentList}/`)) {
      const suffix = path.slice(ELEARNING_STUDENT_SESSIONS_ROUTE.length + "/list".length);
      const target = `${ELEARNING_STUDENT_SESSIONS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (lower === mentorList || lower.startsWith(`${mentorList}/`)) {
      const suffix = path.slice(ELEARNING_MENTOR_SESSIONS_ROUTE.length + "/list".length);
      const target = `${ELEARNING_MENTOR_SESSIONS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (lower === contactList || lower.startsWith(`${contactList}/`)) {
      const suffix = path.slice(ELEARNING_CONTACT_SESSIONS_ROUTE.length + "/list".length);
      const target = `${ELEARNING_CONTACT_SESSIONS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }

    const webStudent = /\/elearning\/student\/sessions(\/|$)/i;
    const webMentor = /\/elearning\/mentor\/sessions(\/|$)/i;
    const webContact = /\/elearning\/contact\/sessions(\/|$)/i;
    if (webStudent.test(lower)) {
      const suffix = path.replace(/^.*\/elearning\/student\/sessions/i, "");
      const target = `${ELEARNING_STUDENT_SESSIONS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (webMentor.test(lower)) {
      const suffix = path.replace(/^.*\/elearning\/mentor\/sessions/i, "");
      const target = `${ELEARNING_MENTOR_SESSIONS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (webContact.test(lower)) {
      const suffix = path.replace(/^.*\/elearning\/contact\/sessions/i, "");
      const target = `${ELEARNING_CONTACT_SESSIONS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }

    if (lower === ELEARNING_STUDENT_SESSIONS_ROUTE.toLowerCase()) {
      return queryPart ? `${ELEARNING_STUDENT_SESSIONS_ROUTE}?${queryPart}` : ELEARNING_STUDENT_SESSIONS_ROUTE;
    }
    if (lower === ELEARNING_MENTOR_SESSIONS_ROUTE.toLowerCase()) {
      return queryPart ? `${ELEARNING_MENTOR_SESSIONS_ROUTE}?${queryPart}` : ELEARNING_MENTOR_SESSIONS_ROUTE;
    }
    if (lower === ELEARNING_CONTACT_SESSIONS_ROUTE.toLowerCase()) {
      return queryPart ? `${ELEARNING_CONTACT_SESSIONS_ROUTE}?${queryPart}` : ELEARNING_CONTACT_SESSIONS_ROUTE;
    }
    if (lower === ELEARNING_HUB_SESSIONS_ROUTE.toLowerCase()) {
      const target = resolveMobileElearningSessionsRoute(
        this.profileStore.profile(),
        this.profileStore.activeTenantAppUid(),
        this.profileStore.activeApplication()?.Code
      );
      return queryPart ? `${target}?${queryPart}` : target;
    }

    return route;
  }

  /** URLs web `/elearning/student/surveys/list` → `/mobile/elearning/student/surveys`. */
  private normalizeElearningLearningCampaignUrl(route: string): string {
    const [pathPart, queryPart] = route.split("?");
    const path = pathPart.replace(/\/+$/, "");
    const lower = path.toLowerCase();

    const studentList = `${ELEARNING_STUDENT_SURVEYS_ROUTE}/list`.toLowerCase();
    const mentorList = `${ELEARNING_MENTOR_SURVEYS_ROUTE}/list`.toLowerCase();
    const contactList = `${ELEARNING_CONTACT_SURVEYS_ROUTE}/list`.toLowerCase();
    if (lower === studentList || lower.startsWith(`${studentList}/`)) {
      const suffix = path.slice(ELEARNING_STUDENT_SURVEYS_ROUTE.length + "/list".length);
      const target = `${ELEARNING_STUDENT_SURVEYS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (lower === mentorList || lower.startsWith(`${mentorList}/`)) {
      const suffix = path.slice(ELEARNING_MENTOR_SURVEYS_ROUTE.length + "/list".length);
      const target = `${ELEARNING_MENTOR_SURVEYS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (lower === contactList || lower.startsWith(`${contactList}/`)) {
      const suffix = path.slice(ELEARNING_CONTACT_SURVEYS_ROUTE.length + "/list".length);
      const target = `${ELEARNING_CONTACT_SURVEYS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }

    const webStudent = /\/elearning\/student\/surveys(\/|$)/i;
    const webMentor = /\/elearning\/mentor\/surveys(\/|$)/i;
    const webContact = /\/elearning\/contact\/surveys(\/|$)/i;
    if (webStudent.test(lower)) {
      const suffix = path.replace(/^.*\/elearning\/student\/surveys/i, "").replace(/\/player\/?$/i, "");
      const target = `${ELEARNING_STUDENT_SURVEYS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (webMentor.test(lower)) {
      const suffix = path.replace(/^.*\/elearning\/mentor\/surveys/i, "").replace(/\/player\/?$/i, "");
      const target = `${ELEARNING_MENTOR_SURVEYS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }
    if (webContact.test(lower)) {
      const suffix = path.replace(/^.*\/elearning\/contact\/surveys/i, "").replace(/\/player\/?$/i, "");
      const target = `${ELEARNING_CONTACT_SURVEYS_ROUTE}${suffix}`;
      return queryPart ? `${target}?${queryPart}` : target;
    }

    if (lower === ELEARNING_STUDENT_SURVEYS_ROUTE.toLowerCase()) {
      return queryPart ? `${ELEARNING_STUDENT_SURVEYS_ROUTE}?${queryPart}` : ELEARNING_STUDENT_SURVEYS_ROUTE;
    }
    if (lower === ELEARNING_MENTOR_SURVEYS_ROUTE.toLowerCase()) {
      return queryPart ? `${ELEARNING_MENTOR_SURVEYS_ROUTE}?${queryPart}` : ELEARNING_MENTOR_SURVEYS_ROUTE;
    }
    if (lower === ELEARNING_CONTACT_SURVEYS_ROUTE.toLowerCase()) {
      return queryPart ? `${ELEARNING_CONTACT_SURVEYS_ROUTE}?${queryPart}` : ELEARNING_CONTACT_SURVEYS_ROUTE;
    }

    return route;
  }

  private normalizeMobileMenuUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) {
      return trimmed;
    }
    const base = MOBILE_BASE_PATH.toLowerCase();
    const lower = trimmed.toLowerCase();
    if (lower === base || lower.startsWith(`${base}/`)) {
      return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    }
    return mobilePath(trimmed.replace(/^\/+/, ""));
  }

  private normalizeComparableUrl(url: string): string {
    const [path] = this.normalizeMobileMenuUrl(url).split("?");
    const normalized = path.replace(/\/+$/, "");
    return normalized.toLowerCase();
  }

  private findDrawerSection(menuUid: string): MobileDrawerSection | null {
    const normalizedUid = menuUid.trim().toLowerCase();
    return (
      this.drawerSections().find((section) => section.Uid.trim().toLowerCase() === normalizedUid) ?? null
    );
  }

  private canAccessMenuItem(
    profileData: ReturnType<MobileProfileStore["profile"]>,
    activeTenantAppUid: string | null,
    identity: { IsRoot?: boolean; IsAdmin?: boolean } | undefined,
    item: TenantSideMenuItem
  ): boolean {
    if (!profileData) {
      return true;
    }
    if (identity?.IsRoot || identity?.IsAdmin) {
      const hasProfiles = !!profileData.Identity?.Profils?.length;
      if (!hasProfiles) {
        return true;
      }
    }
    return mobileCanAccessMenuItem(profileData, activeTenantAppUid, item);
  }

  async selectApplication(tenantAppUid: string): Promise<void> {
    const switched = await this.profileService.switchActiveApp(tenantAppUid);
    if (!switched) {
      this.profileStore.setActiveTenantAppUid(tenantAppUid);
      await Promise.all([
        this.cacheService.saveAvailableApps(this.profileStore.availableApplications()),
        this.cacheService.saveActiveTenantAppUid(tenantAppUid),
      ]);
    }
  }

  async clearActiveApplication(): Promise<void> {
    this.profileStore.setActiveTenantAppUid(null);
    await Promise.all([
      this.cacheService.saveAvailableApps(this.profileStore.availableApplications()),
      this.cacheService.saveActiveTenantAppUid(null),
    ]);
  }

  getDefaultRoute(): string {
    const active = this.profileStore.activeApplication();
    if (!active) {
      return mobilePath("launcher");
    }

    const appCode = (active as { Code?: string }).Code?.trim().toLowerCase();
    if (appCode === ACADEMY_MOBILE_APP_CODE || appCode === LEGACY_ELEARNING_MOBILE_APP_CODE) {
      return resolveMobileElearningSessionsRoute(
        this.profileStore.profile(),
        this.profileStore.activeTenantAppUid(),
        appCode
      );
    }

    const sections = this.drawerSections();
    const defaultHome = sections
      .flatMap((section) => section.Items.map((item) => ({ sectionOrder: section.Order, item })))
      .sort(
        (left, right) =>
          left.sectionOrder - right.sectionOrder || left.item.Order - right.item.Order
      )
      .map((entry) => entry.item)
      .find((item) => item.IsDefaultHome);
    if (defaultHome?.Route) {
      return defaultHome.Route;
    }

    const taskbar = this.bottomNavItems().filter((item) => item.Route !== profileNavItem.Route);
    if (taskbar[0]?.Route) {
      return taskbar[0].Route;
    }

    const firstMenuItem = sections
      .sort((left, right) => left.Order - right.Order)
      .flatMap((section) => [...section.Items].sort((left, right) => left.Order - right.Order))[0];
    return firstMenuItem?.Route ?? mobilePath("launcher");
  }
}
